import { Box } from '@chakra-ui/layout';
import { type ReactNode, useCallback, useEffect } from 'react';

import type { CompositionV2DataQuery } from '@revelio/data-access';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { CompositionRawDataProvider } from '../../DataProvider/composition/CompositionRawDataProvider';
import {
  DataProviderQueryStatus,
  DataType,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../../DataProvider/types';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import { usePageState } from '../../PageState/PageStateProvider';
import type { SubfilterTree } from '../../PageState/types';
import { Subfilter } from '../../Subfilter/Subfilter';
import { ChartError } from '../ChartError';
import { ChartFallback } from '../ChartFallback';
import { ChartLoader } from '../ChartLoader';
import { useChartMenuContext } from '../chart-menu/ChartMenuContext';
import { Line } from '../Line';
import { useShareOfSegmentSubfilters } from './useSubFilter';

type ShareOfSegmentProps = {
  subfilterList: SubfilterTree[];
  selectedSegments: SubfilterTree[];
  chartData: TimeseriesDataSet | null;
  onSubmitSubfilter: (selections: SubfilterTree[]) => void;
};

const EMPTY_SELECTED_SEGMENTS: SubfilterTree[] = [];

type SegmentSelectionControls = {
  selectedSegments: SubfilterTree[];
  setSelections: (
    selections: SubfilterTree[],
    options?: { updateInitial?: boolean }
  ) => void;
};

export const useSegmentSelections = (
  plotId: string
): SegmentSelectionControls => {
  const { pageState, dispatch } = usePageState();
  const selectedSegments =
    pageState.selections?.[plotId] ?? EMPTY_SELECTED_SEGMENTS;

  const setSelections = useCallback(
    (
      selections: SubfilterTree[],
      { updateInitial = false }: { updateInitial?: boolean } = {}
    ) => {
      dispatch({
        type: 'SET_SELECTIONS',
        payload: {
          plotId,
          selections,
          updateInitial,
        },
      });
    },
    [dispatch, plotId]
  );

  return { selectedSegments, setSelections };
};

export const ShareOfSegment = ({
  subfilterList,
  selectedSegments,
  chartData,
  onSubmitSubfilter,
}: ShareOfSegmentProps) => {
  const { setDataProviderSet } = useChartMenuContext();

  useEffect(() => {
    if (chartData && chartData.data.length > 0 && selectedSegments.length > 0) {
      setDataProviderSet(chartData);
    }
  }, [chartData, selectedSegments, setDataProviderSet]);

  return (
    <Box height="100%" position="relative">
      <Subfilter
        subfilterList={subfilterList}
        selectedItems={selectedSegments}
        onSubmit={onSubmitSubfilter}
        position="absolute"
        top="-32px"
        right="22px"
        openByDefault={subfilterList.some(
          (item) => item?.children && item.children.length > 0
        )}
      />
      <Line
        data={chartData?.data ?? []}
        metricOverride="share_of_headcount"
        selectedSegments={selectedSegments}
      />
    </Box>
  );
};

export const ShareOfSegmentChartShell = ({
  status,
  haveSelectionsBeenModified,
  children,
}: {
  status: DataProviderQueryStatus;
  haveSelectionsBeenModified?: boolean;
  children: ReactNode;
}) => {
  if (haveSelectionsBeenModified) {
    const shouldShowLoader = [
      DataProviderQueryStatus.Idle,
      DataProviderQueryStatus.Fetching,
    ].includes(status);

    return (
      <Box position="relative" width="100%" height="100%">
        {!shouldShowLoader && children}
        {status === DataProviderQueryStatus.Error ? <ChartError /> : null}
        {shouldShowLoader ? <ChartLoader /> : null}
      </Box>
    );
  }

  return <ChartFallback status={status}>{children}</ChartFallback>;
};

export const ShareOfSegmentChart = ({
  primaryDimension,
  secondaryDimension,
  plotId,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: CompositionSecondaryDimensionValues;
  plotId: string;
}) => {
  const { selectedSegments, setSelections } = useSegmentSelections(plotId);
  const { subfilterList, isTreeSubfilter, defaultSelection } =
    useShareOfSegmentSubfilters(secondaryDimension);

  useEffect(() => {
    if (!subfilterList.length || !defaultSelection) return;

    const hasSelections = selectedSegments.length > 0;
    const selectionsValid = hasSelections
      ? areSelectionsValid(selectedSegments, subfilterList, isTreeSubfilter)
      : false;

    if (!hasSelections || !selectionsValid) {
      setSelections([defaultSelection], { updateInitial: true });
    }
  }, [
    subfilterList,
    defaultSelection,
    selectedSegments,
    isTreeSubfilter,
    setSelections,
  ]);

  const handleSubmitSubfilter = useCallback(
    (selections: SubfilterTree[]) => setSelections(selections),
    [setSelections]
  );

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      isTimeseries
      getSelectionBreakdowns
    >
      {({ data, status, haveSelectionsBeenModified }) => {
        const chartData = buildShareOfSegmentChartData(
          data?.composition_v2 || undefined,
          secondaryDimension,
          selectedSegments
        );

        return (
          <ShareOfSegmentChartShell
            haveSelectionsBeenModified={haveSelectionsBeenModified}
            status={status}
          >
            <ShareOfSegment
              chartData={chartData}
              onSubmitSubfilter={handleSubmitSubfilter}
              selectedSegments={selectedSegments}
              subfilterList={subfilterList}
            />
          </ShareOfSegmentChartShell>
        );
      }}
    </CompositionRawDataProvider>
  );
};

export const areSelectionsValid = (
  selectedSegments: SubfilterTree[],
  subfilterList: SubfilterTree[],
  isTreeSubfilter: boolean
) => {
  if (!selectedSegments.length) return false;
  if (!subfilterList.length) return true;

  const comparisonList = isTreeSubfilter
    ? subfilterList.flatMap((item) => item.children ?? [])
    : subfilterList;

  if (!comparisonList.length) return false;

  return selectedSegments.every((segment) =>
    comparisonList.some(
      (item) => `${item.id}_${item.name}` === `${segment.id}_${segment.name}`
    )
  );
};

const buildShareOfSegmentChartData = (
  data: NonNullable<CompositionV2DataQuery['composition_v2']> | undefined,
  secondaryDimension: CompositionSecondaryDimensionValues,
  selectedSegments: SubfilterTree[]
): TimeseriesDataSet | null => {
  if (!data?.entities || selectedSegments.length === 0) return null;

  const dateRefs = data.refs?.months ?? [];
  const selectedSegmentIds = new Set(
    selectedSegments
      .map((segment) => segment?.id?.split('_')?.slice(-1)?.[0])
      .filter((id): id is string => Boolean(id))
  );

  const compositionData: TimeseriesDataSet['data'] =
    data.entities?.map((entity) => {
      const dim = entity.metadata?.short_name;
      const metricData = entity.metrics?.[secondaryDimension];
      const selectedMetrics =
        metricData?.filter((segment) =>
          selectedSegmentIds.has(`${segment.metadata.id}`)
        ) ?? [];

      if (!selectedMetrics.length) {
        return { dim, timeseries: [] };
      }

      const timeseries: TimeseriesDataPoint[] = [];
      const firstMetricTimeseriesLength =
        selectedMetrics[0]?.timeseries?.length ?? 0;

      for (
        let timePointIndex = 0;
        timePointIndex < firstMetricTimeseriesLength;
        timePointIndex++
      ) {
        for (
          let metricIndex = 0;
          metricIndex < selectedMetrics.length;
          metricIndex++
        ) {
          const metric = selectedMetrics[metricIndex];
          const timePoint = metric?.timeseries?.[timePointIndex];
          if (!timePoint) continue;

          const share = (timePoint.share ?? 0) / 100;
          const count = timePoint.count ?? 0;
          const date = dateRefs[timePoint.id];
          if (!date) continue;

          const existing = timeseries[timePointIndex];
          if (existing) {
            existing.value =
              (typeof existing.value === 'number' ? existing.value : 0) + share;
            existing.secondaryValue =
              (typeof existing.secondaryValue === 'number'
                ? existing.secondaryValue
                : 0) + count;
          } else {
            timeseries[timePointIndex] = {
              date,
              value: share,
              secondaryValue: count,
            };
          }
        }
      }

      return {
        dim,
        timeseries: timeseries.map((point) => ({
          ...point,
          value:
            typeof point.value === 'number'
              ? Math.round(point.value * 10000) / 10000
              : point.value,
        })),
      };
    }) ?? [];

  return { type: DataType.TIMESERIES, data: compositionData };
};
